import { UltrabufEntries, type ICommandData, MutationType } from '@tencent/slide-sdk-pb-model';
import lowerFirst from 'lodash/lowerFirst';
import { Base64String } from '../utils/base64-utils';
import { createMutations } from '../mutation/mutation-factory';
import { MutationTypeKeyMap } from '../constants';
import { ValidMutationData, type IValidMutationData } from '../mutation/base';
import { SlideCodec } from './slide-codec';
import type { IMutationData } from '@tencent/slide-sdk-pb-model';
import type { Mutation } from '../mutation/base/base-mutation';

export class CommandData {
    public static from(data: ICommandData) {
        return new CommandData(data);
    }

    public static fromBufferOrBase64(buffer: Uint8Array | string) {
        const data = SlideCodec.codec.decode(buffer, UltrabufEntries.CommandData);
        return new CommandData(data);
    }

    public static fromMutations(mutations: Mutation[]) {
        return this.fromValidMutationData(mutations.map((mut) => mut.toData()));
    }

    public static fromMutationData(mutations: IMutationData[]) {
        return new CommandData({
            mutations,
        });
    }

    public static fromValidMutationData(mutations: IValidMutationData[]) {
        return new CommandData({
            mutations: mutations.map((mutation) => ({
                type: mutation.type,
                [lowerFirst(MutationType[mutation.type])]: mutation,
            })),
        });
    }

    private data: ICommandData;
    private mutations?: Mutation[];

    public constructor(data: ICommandData) {
        this.data = { mutations: [], ...data };
    }

    public toBase64String(): string {
        const buf = SlideCodec.codec.encode(this.data, UltrabufEntries.CommandData);
        return Base64String.fromUint8Array(buf);
    }

    public getCommandData() {
        return this.data;
    }

    public getMutations(): Mutation[] {
        if (!this.mutations) {
            this.mutations = createMutations(this.getValidMutationData());
        }

        return this.mutations ?? [];
    }

    public getValidMutationData(): IValidMutationData[] {
        return (this.data.mutations ?? []).map(({ type, ...content }) => {
            if (!type) {
                throw new Error(`mutation type is not specified: ${JSON.stringify(content)}`);
            }

            return { ...content[MutationTypeKeyMap[type]], type };
        });
    }

    public getMutationData(): IMutationData[] {
        return this.data.mutations ?? [];
    }

    public validate(): IMutationData | undefined {
        return this.getValidMutationData().find((mutation) => !ValidMutationData(mutation));
    }
}
